import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '../shared/services/login.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  public loginFormGroup!: FormGroup;
  public loggingIn = false;
  private redirectUrl!: string;
  passwordFieldType: string = 'password';

  constructor(
    public router: Router,
    private fb: FormBuilder,
    private login: LoginService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
  ) {
    this.loginFormGroup = this.fb.group({
      username: new FormControl<string | null>(null, Validators.required),
      password: new FormControl<string | null>(null, Validators.required),
      rememberMe: [false],
    });
  }

  ngOnInit(): void {
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl') || '/';
    if (this.login.isTokenValid()) {
      this.router.navigate(['/nash-pro'], { replaceUrl: true });
    }
  }

  public get usernameField(): FormControl<string | null> {
    return this.loginFormGroup.get('username') as FormControl<string | null>;
  }

  public get passwordField(): FormControl<string | null> {
    return this.loginFormGroup.get('password') as FormControl<string | null>;
  }

  public logIn(): void {
    if (this.loginFormGroup.invalid) {
      return;
    }

    this.loggingIn = true;

    const username = this.usernameField.value ?? '';
    const password = this.passwordField.value ?? '';

    const loginObservable = this.login.login(username, password);

    loginObservable.subscribe({
      next: (res) => {
        if (res.authentication?.token) {
          localStorage.setItem('token', res.authentication.token);
        }
        localStorage.setItem('expires_at', String(res.authentication?.validTo));
        this.login.setUser(res.feUser);
        this.router.navigateByUrl('/nash-pro');
      },
      error: () => {
        this.loggingIn = false;
        const errorMsg = this.translate.instant('ERROR_MESSAGES.INVALID_CREDENTIALS');
        this.showErrorNotification(errorMsg);
      },
    });
  }

  togglePasswordVisibility(): void {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  private showErrorNotification(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
    });
  }

  get requestAccessMailto(): string {
    const supportEmail = 'Nashpro.support@irco.com';

    const subject = encodeURIComponent(this.translate.instant('LOGIN.REQUEST_ACCESS'));

    // Try to use username if it looks like an email
    const rawUser = (this.loginFormGroup.get('username')?.value || '').trim();
    const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawUser) ? rawUser : '';

    const describe = this.translate.instant('LOGIN.REQUEST_ACCESS_BODY');
    const pleaseEnter = this.translate.instant('LOGIN.PLEASE_ENTER_EMAIL');

    const requesterLine = `Requester email: ${emailLike || `(${pleaseEnter})`}`;
    const passwordLine = 'password:';
    const userRole = `Please describe why you need access to Nash Pro:`;
    const urlLine = `URL: ${this.router.url}`;

    const body = encodeURIComponent(`${describe}\n\n${requesterLine}\n${passwordLine}\n${userRole}\n${urlLine}\n`);

    return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  }
}
